import { useState, useEffect } from 'react';
import { Bell, UserPlus, UserCheck, UserX, Loader2, Eye, Trash2, CheckCheck, MessageSquare, Heart } from 'lucide-react';
import notificationService from '../services/api/notification.service';
import connectionService from '../services/api/connection.service';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../utils/imageUtils';

const Requests = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'connection', 'follower'
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getMyNotifications(1, 50, false);
      if (response.success) {
        const notificationsData = response.data?.notifications || response.data || [];
        setNotifications(Array.isArray(notificationsData) ? notificationsData : []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (connectionId, notificationId) => {
    try {
      setProcessingId(notificationId);
      const response = await connectionService.acceptConnectionRequest(connectionId);
      if (response.success) {
        toast.success('Connection request accepted');
        // Remove notification from UI immediately
        setNotifications(notifications.filter(n => n.id !== notificationId));
        await notificationService.markAsRead(notificationId);
      }
    } catch (error) {
      console.error('Failed to accept request:', error);
      toast.error('Failed to accept connection request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (connectionId, notificationId) => {
    try {
      setProcessingId(notificationId);
      const response = await connectionService.rejectConnectionRequest(connectionId);
      if (response.success) {
        toast.success('Connection request rejected');
        // Remove notification from UI immediately
        setNotifications(notifications.filter(n => n.id !== notificationId));
        await notificationService.markAsRead(notificationId);
      }
    } catch (error) {
      console.error('Failed to reject request:', error);
      toast.error('Failed to reject connection request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, isRead: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, isRead: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications(notifications.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    } else if (notification.relatedUserId) {
      navigate(`/portfolio/${notification.relatedUserId}`);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'connection_request':
        return <UserPlus className="w-5 h-5 text-blue-600" />;
      case 'connection_accepted':
        return <UserCheck className="w-5 h-5 text-green-600" />;
      case 'new_follower':
        return <Heart className="w-5 h-5 text-pink-600" />;
      case 'portfolio_comment':
        return <MessageSquare className="w-5 h-5 text-purple-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const filteredNotifications = Array.isArray(notifications) ? notifications.filter(n => {
    if (filter === 'unread') return !n.isRead;
    if (filter === 'connection') return n.type === 'connection_request' || n.type === 'connection_accepted';
    if (filter === 'follower') return n.type === 'new_follower';
    if (filter === 'comments') return n.type === 'portfolio_comment';
    return true;
  }) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 py-4 px-2 ">
      <div className="max-w-8xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="bg-white px-6 py-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
                <p className="text-gray-600 mt-1 text-sm">Stay updated with all your activities</p>
              </div>
              {Array.isArray(notifications) && notifications.some(n => !n.isRead) && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary-500 hover:bg-secondary-600 text-white rounded-lg transition-colors"
                >
                  <CheckCheck className="w-4 h-4" />
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          <div className="p-6">
            {/* Filter tabs */}
            <div className="mb-6 flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'unread', label: 'Unread' },
                { key: 'connection', label: 'Connections' },
                { key: 'follower', label: 'Followers' },
                { key: 'comments', label: 'Comments & Questions' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === key
                      ? 'bg-secondary-500 text-white'
                      : 'bg-secondary-50 text-gray-700 hover:bg-secondary-100'
                  }`}
                >
                  {label}
                  {key === 'unread' && Array.isArray(notifications) && notifications.filter(n => !n.isRead).length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                      {notifications.filter(n => !n.isRead).length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {filteredNotifications.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <Bell className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
                <p className="text-gray-500">
                  {filter === 'unread' 
                    ? 'You have no unread notifications'
                    : 'Your notifications will appear here'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="border border-gray-200 bg-white rounded-xl p-4 transition-all cursor-pointer hover:shadow-md"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {notification.relatedUser?.profilePicture ? (
                          <img
                            src={notification.relatedUser.profilePicture}
                            alt=""
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center shadow-md"
                          style={{ 
                            display: notification.relatedUser?.profilePicture ? 'none' : 'flex',
                            backgroundColor: '#1a2642'
                          }}
                        >
                          <Bell className="w-6 h-6 text-white" />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-base font-semibold text-gray-900">
                                {notification.title}
                              </h3>
                              {!notification.isRead && (
                                <span className="w-2 h-2 bg-primary-600 rounded-full"></span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              {new Date(notification.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(notification.id);
                            }}
                            className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Action buttons for connection requests - only show for unread */}
                        {notification.type === 'connection_request' && notification.relatedEntityId && !notification.isRead && (
                          <div className="flex items-center gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleAccept(notification.relatedEntityId, notification.id)}
                              disabled={processingId === notification.id}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {processingId === notification.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserCheck className="w-4 h-4" />
                              )}
                              Accept
                            </button>
                            <button
                              onClick={() => handleReject(notification.relatedEntityId, notification.id)}
                              disabled={processingId === notification.id}
                              className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              <UserX className="w-4 h-4" />
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Requests;
